// src/PrivateRoute.jsx
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../Auth/AuthContext';
import { useEffect, useState } from 'react';

const LoadingFallback = () => (
  <div className="flex items-center justify-center h-screen bg-gray-50">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#e85b5e] mx-auto"></div>
      <p className="mt-4 text-gray-600">Loading Customer 360...</p>
    </div>
  </div>
);

const PrivateRoute = () => {
  const { isAuthenticated, isLoading, user, hasAccessToPath, getFirstAvailablePath } = useAuth();
  const location = useLocation();
  const [redirectPath, setRedirectPath] = useState(null);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    let mounted = true;

    const checkAccess = async () => {
      if (!isLoading) {
        if (isAuthenticated && user) {
          const currentPath = location.pathname;
          
          // For admin with empty accessPath, allow access to all routes
          if (user.role === 'admin' && (!user.accessPath || user.accessPath.length === 0)) {
            // Admin with empty accessPath can access everything
            console.log('Admin with empty accessPath - allowing all routes');
            if (mounted) {
              setIsChecking(false);
            }
            return;
          }
          
          // Check if user has access to the current path
          const hasAccess = hasAccessToPath(currentPath);
          
          if (!hasAccess && mounted) {
            // Redirect to first available path
            const firstPath = getFirstAvailablePath();
            console.log(`User doesn't have access to ${currentPath}, redirecting to ${firstPath}`);
            setRedirectPath(firstPath);
          }
        }
        if (mounted) {
          setIsChecking(false);
        }
      }
    };

    // Add small delay to ensure everything is loaded
    setTimeout(checkAccess, 100);

    return () => {
      mounted = false;
    };
  }, [isLoading, isAuthenticated, user, location.pathname, hasAccessToPath, getFirstAvailablePath]);

  if (isLoading || isChecking) {
    return <LoadingFallback />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/signin" state={{ from: location }} replace />;
  }

  if (redirectPath) {
    return <Navigate to={redirectPath} replace />;
  }

  return <Outlet />;
};

export default PrivateRoute;